import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { Button } from '@/components/ui/button';
import { ArrowRight, PenTool } from 'lucide-react';
import { API_URL } from '@/utils/api';

const attributionLabel = (item) => {
  if (item.attribution_type === 'first_name') return item.attribution_value || 'A HousePlanDirect user';
  if (item.attribution_type === 'business_name') return item.attribution_value || 'A HousePlanDirect user';
  return 'Anonymous';
};

const HpdDrawGallery = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "HPD Draw Gallery", path: "/hpd-draw-gallery" }
  ];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/gallery-submissions/approved`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="hpd-draw-gallery"
        defaultTitle="DIY Floor Plan Gallery | Made with HousePlanDirect"
        defaultDescription="Real floor plans voluntarily shared by HousePlanDirect users, drawn and measured with HPD Draw's free online floor plan creator and measurement tool."
        aiSummary="A public gallery of real DIY floor plans shared by HousePlanDirect users, made with the free online floor plan creator and measurement tool (HPD Draw). Plans are voluntarily submitted and moderated before publishing; HousePlanDirect does not normally store users' drawings."
        keywords="floor plan gallery, DIY floor plan examples, made with HousePlanDirect, floor plan creator examples"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" data-testid="hpd-draw-gallery-title">
            DIY Floor Plan Gallery
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            These are real plans voluntarily shared by HousePlanDirect users after drawing or measuring their own floor plan with HPD Draw. We don't normally store or see users' drawings — everything below was shared with explicit permission and reviewed before publishing.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="hpd-draw-gallery-empty">
            No plans have been shared yet — be the first! Draw a plan and choose to share it after your export.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="hpd-draw-gallery-grid">
            {items.map((item) => (
              <div key={item.id} className="rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-testid={`hpd-draw-gallery-item-${item.id}`}>
                <img
                  src={`${API_URL}/gallery-submissions/image/${item.filename}`}
                  alt={item.admin_alt_text || item.admin_title || item.title || 'DIY floor plan made with HousePlanDirect'}
                  className="w-full h-56 object-contain bg-gray-50"
                  loading="lazy"
                  width={480}
                  height={320}
                />
                <div className="p-4">
                  <h2 className="font-semibold text-gray-900" data-testid={`hpd-draw-gallery-item-title-${item.id}`}>
                    {item.admin_title || item.title || 'Floor plan made with HousePlanDirect'}
                  </h2>
                  {item.admin_category && (
                    <span className="inline-block mt-1 text-xs rounded-full bg-blue-50 text-blue-700 px-2 py-0.5">{item.admin_category}</span>
                  )}
                  <p className="mt-2 text-xs text-gray-500">Shared by {attributionLabel(item)}</p>
                  {item.feedback_comment && (
                    <p className="mt-2 text-sm italic text-gray-600 border-l-2 border-gray-200 pl-3">&ldquo;{item.feedback_comment}&rdquo;</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16 text-center bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Draw your own floor plan</h2>
          <p className="text-lg mb-8 text-emerald-100">
            Free to create and edit. Unlock the finished project for just £2.99.
          </p>
          <Link to="/floor-plan-creator">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-gray-100" data-testid="hpd-draw-gallery-cta-btn">
              <PenTool className="mr-2 h-5 w-5" /> Draw your own floor plan
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HpdDrawGallery;
